import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  BarChart3, 
  Package, 
  FileText, 
  Tag, 
  TrendingUp, 
  ChevronDown, 
  ChevronRight,
  ArrowLeft,
  Droplet,
  Cpu,
  Server,
  Radio,
  Database,
  Wifi,
  Activity,
  Gauge,
  Zap,
  Box,
  ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for industrial products
const iconMap = {
  droplet: Droplet,
  cpu: Cpu,
  server: Server,
  radio: Radio,
  database: Database,
  wifi: Wifi,
  activity: Activity,
  gauge: Gauge,
  zap: Zap,
  box: Box,
  package: Package
};

const colorClasses = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  pink: 'bg-pink-100 text-pink-700 border-pink-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200'
};

const ProductCoverageReport = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    fetchCoverageReport();
  }, []);

  const fetchCoverageReport = async () => {
    try {
      const response = await axios.get(`${API}/api/v2/product-coverage-report`);
      setReport(response.data);
    } catch (error) {
      console.error('Failed to fetch coverage report:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductDetail = async (productId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/api/v2/product-coverage-report/${productId}`);
      setProductDetail(response.data);
    } catch (error) {
      console.error('Failed to fetch product detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleProductExpand = (productId) => {
    setExpandedProducts(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  const handleProductClick = (productId) => {
    setSelectedProduct(productId);
    fetchProductDetail(productId);
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Package;
    return IconComponent;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-32 bg-slate-200 rounded-xl"></div>
              ))}
            </div>
            <div className="h-96 bg-slate-200 rounded-xl"></div>
          </div>
        </div>
      </div>
    );
  }

  // Product Detail View
  if (selectedProduct && productDetail) {
    const IconComponent = getIcon(productDetail.product?.icon);
    
    return (
      <div className="min-h-screen bg-slate-50 p-8" data-testid="product-detail-view">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Back Button */}
          <Button 
            variant="ghost" 
            onClick={() => { setSelectedProduct(null); setProductDetail(null); }}
            className="mb-4"
            data-testid="back-to-report-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Coverage Report
          </Button>

          {/* Product Header */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${colorClasses[productDetail.product?.color] || colorClasses.blue}`}>
                  <IconComponent className="w-8 h-8" />
                </div>
                <div>
                  <CardTitle className="text-2xl">{productDetail.product?.name}</CardTitle>
                  <CardDescription>{productDetail.product?.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-600">RFPs Tagged</p>
                      <p className="text-2xl font-bold text-blue-700">{productDetail.total_rfps}</p>
                    </div>
                  </div>
                </div>
                <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                  <div className="flex items-center gap-3">
                    <Tag className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-sm text-slate-600">Total Tags</p>
                      <p className="text-2xl font-bold text-green-700">{productDetail.total_tags}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* RFP Breakdown */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-600" />
                RFP Breakdown
              </CardTitle>
              <CardDescription>All RFPs where this product has been tagged</CardDescription>
            </CardHeader>
            <CardContent>
              {productDetail.rfp_breakdown?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Tag className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No RFPs tagged with this product yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {productDetail.rfp_breakdown?.map((rfp) => (
                    <div 
                      key={rfp.rfp_id} 
                      className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:border-blue-300 transition-colors"
                      data-testid={`rfp-breakdown-${rfp.rfp_id}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-500" />
                          <div>
                            <h4 className="font-medium text-slate-800">{rfp.rfp_name}</h4>
                            <p className="text-xs text-slate-500">{rfp.tags?.length} sections tagged</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={rfp.rfp_status === 'analyzed' ? 'default' : 'secondary'}>
                            {rfp.rfp_status}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/tender/${rfp.rfp_id}`)}
                            data-testid={`view-rfp-${rfp.rfp_id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Tagged Sections */}
                      <div className="flex flex-wrap gap-2 mt-2">
                        {rfp.tags?.slice(0, 5).map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {tag.section_name || 'Section'} 
                            {tag.subsection_name && ` > ${tag.subsection_name}`}
                          </Badge>
                        ))}
                        {rfp.tags?.length > 5 && (
                          <Badge variant="outline" className="text-xs bg-slate-100">
                            +{rfp.tags.length - 5} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Report View
  return (
    <div className="min-h-screen bg-slate-50 p-8" data-testid="product-coverage-report">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Product Coverage Report</h1>
            <p className="text-slate-500 mt-1">Track which products are tagged across all your RFPs</p>
          </div>
          <Button onClick={fetchCoverageReport} variant="outline" data-testid="refresh-report-btn">
            Refresh
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total RFPs</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="total-rfps">
                    {report?.summary?.total_rfps || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Products</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="total-products">
                    {report?.summary?.total_products || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <Tag className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Tags</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="total-tags">
                    {report?.summary?.total_tags || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Products Tagged</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="products-tagged">
                    {report?.summary?.products_with_tags || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Coverage</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="coverage-percentage">
                    {report?.summary?.coverage_percentage || 0}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coverage Progress */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Product Catalog Coverage</CardTitle>
            <CardDescription>
              {report?.summary?.products_with_tags || 0} of {report?.summary?.total_products || 0} products have been tagged in RFPs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Progress 
              value={report?.summary?.coverage_percentage || 0} 
              className="h-3"
            />
          </CardContent>
        </Card>

        {/* Product List */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-slate-600" />
              Product Coverage Details
            </CardTitle>
            <CardDescription>Click on a product to see detailed RFP breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {report?.products?.map((product) => {
                const IconComponent = getIcon(product.product_icon);
                const isExpanded = expandedProducts[product.product_id];
                
                return (
                  <div 
                    key={product.product_id}
                    className="border border-slate-200 rounded-xl overflow-hidden hover:border-blue-300 transition-colors"
                    data-testid={`product-row-${product.product_id}`}
                  >
                    <div 
                      className="flex items-center justify-between p-4 bg-white cursor-pointer"
                      onClick={() => handleProductClick(product.product_id)}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${colorClasses[product.product_color] || colorClasses.blue}`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{product.product_name}</h3>
                          <p className="text-sm text-slate-500">
                            {product.total_rfps_tagged} RFPs, {product.total_sections_tagged} sections tagged
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={product.total_rfps_tagged > 0 ? "default" : "secondary"}
                              className={product.total_rfps_tagged > 0 ? "bg-blue-600" : ""}
                            >
                              {product.total_rfps_tagged} RFPs
                            </Badge>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleProductExpand(product.product_id);
                          }}
                          data-testid={`expand-product-${product.product_id}`}
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Expanded RFP List */}
                    {isExpanded && (
                      <div className="bg-slate-50 border-t border-slate-200 p-4">
                        {product.rfps?.length === 0 ? (
                          <p className="text-sm text-slate-500 text-center py-2">No RFPs tagged yet</p>
                        ) : (
                          <div className="space-y-2">
                            {product.rfps?.map((rfp) => (
                              <div 
                                key={rfp.id}
                                className="flex items-center justify-between bg-white rounded-lg p-3 border border-slate-100"
                              >
                                <div className="flex items-center gap-3">
                                  <FileText className="w-4 h-4 text-slate-400" />
                                  <span className="text-sm font-medium text-slate-700">{rfp.name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs">
                                    {rfp.sections_tagged} sections
                                  </Badge>
                                  <Badge variant={rfp.status === 'analyzed' ? 'default' : 'secondary'} className="text-xs">
                                    {rfp.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              
              {(!report?.products || report.products.length === 0) && (
                <div className="text-center py-12 text-slate-500">
                  <Package className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">No products in catalog</p>
                  <p className="text-sm mt-1">Add products in Settings to start tracking coverage</p>
                  <Button 
                    className="mt-4" 
                    onClick={() => navigate('/settings')}
                    data-testid="go-to-settings-btn"
                  >
                    Go to Settings
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductCoverageReport;
